// src/pages/ExperienceTimeline.tsx
import { Brain, Building, Cctv, ChevronLeft, Cloud, Code, Database, Server, Terminal, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// Dados das experiências (sintetizados)
const experiences = [
  {
    id: 1,
    company: "Joit (Cliente: Mercado Eletrônico)",
    position: "Desenvolvedor .NET",
    period: "Jan 2025 - Abr 2025",
    duration: "4 meses",
    location: "Remota",
    description: "Desenvolvimento de plataforma baseada em agentes cognitivos com IA generativa via Microsoft Semantic Kernel e OpenAI.",
    technologies: [".NET 9", "Semantic Kernel", "OpenAI SDK", "MassTransit", "RabbitMQ", "Redis", "Qdrant", "MongoDB"],
    icon: <Brain className="h-5 w-5" />,
    category: "ai",
    highlights: [
      "Clean Architecture com camadas bem definidas",
      "Sistema projetado para escalar e integrar com serviços internos",
      "Cache distribuído e busca vetorial para IA"
    ]
  },
  {
    id: 2,
    company: "Carglass® Brasil",
    position: "Desenvolvedor Full Stack",
    period: "Mar 2024 - Nov 2024",
    duration: "9 meses",
    location: "Remota",
    description: "Desenvolvimento e manutenção de soluções estratégicas com foco em back-end.",
    technologies: [".NET Core 3.1/8.0", "SQL Server", "RavenDB", "Angular 8", "React", "TypeScript", "Azure DevOps"],
    icon: <Cctv className="h-5 w-5" />,
    category: "fullstack",
    highlights: [
      "Portal do Corretor com tracking de pacotes de comissão",
      "Manutenção de Painel Administrativo e Landing Pages",
      "Integração contínua com Azure DevOps"
    ]
  },
  {
    id: 3,
    company: "act digital (Cliente: Banco BTG Pactual)",
    position: "Analista de Desenvolvimento .NET",
    period: "Jul 2022 - Jan 2024",
    duration: "1 ano 7 meses",
    location: "São Paulo, Brasil",
    description: "Desenvolvimento backend em ambiente de alta performance para área de investimentos e compliance.",
    technologies: [".NET Core 3.1/6.0", "GraphQL", "Microserviços", "AWS", "Kubernetes", "Event Sourcing", "CQRS"],
    icon: <Database className="h-5 w-5" />,
    category: "backend",
    highlights: [
      "Versão 2 do formulário PRP (Perfil de Risco do Investidor)",
      "Sistemas MyCompliance e Matriz de Risco",
      "Ecossistema distribuído com foco em qualidade"
    ]
  },
  {
    id: 4,
    company: "Iterative (Cliente: ConectCar)",
    position: "Analista Programador FullStack",
    period: "Set 2021 - Mar 2022",
    duration: "7 meses",
    location: "Remota",
    description: "Desenvolvimento de soluções para experiência do cliente com foco em performance e segurança.",
    technologies: [".NET Core 3.1/5.0", "SQL Server", "MongoDB", "React", "Azure DevOps"],
    icon: <Zap className="h-5 w-5" />,
    category: "fullstack",
    highlights: [
      "Portal do Cliente ConectCar",
      "Visualização de viagens e resumo financeiro",
      "Integração com múltiplas fontes de dados"
    ]
  },
  {
    id: 5,
    company: "LTM Fidelidade (Cliente: VIVO)",
    position: "Analista Programador Sênior",
    period: "Mar 2020 - Ago 2021",
    duration: "1 ano 6 meses",
    location: "São Paulo",
    description: "Construção da arquitetura e desenvolvimento da plataforma VIVO Valoriza para recompensas e fidelização.",
    technologies: [".NET Core 2.2", "ASP.NET Core", "Dapper", "AngularJS", "Azure Service Bus", "Redis"],
    icon: <Server className="h-5 w-5" />,
    category: "backend",
    highlights: [
      "Arquitetura seguindo Twelve-Factor App e DDD",
      "Implementação de testes unitários",
      "Padrões Singleton e Chain of Responsibility"
    ]
  },
  {
    id: 6,
    company: "Carglass® Nederland",
    position: "Analista Programador Full Stack",
    period: "Set 2019 - Fev 2020",
    duration: "6 meses",
    location: "São Paulo e Região",
    description: "Manutenção do e-commerce e criação do novo Painel BackOffice com arquitetura moderna.",
    technologies: [".NET Core 2.1/3.1", "Dapper", "Angular 8", "JWT", "Azure DevOps"],
    icon: <Cloud className="h-5 w-5" />,
    category: "fullstack",
    highlights: [
      "Criação completa do painel administrativo BackOffice",
      "Autenticação JWT e gestão de sessões",
      "Aplicação de padrões de projeto"
    ]
  },
  {
    id: 7,
    company: "LTM Fidelidade (Cliente: Souza Cruz)",
    position: "Analista Programador .NET",
    period: "Mar 2017 - Jun 2019",
    duration: "2 anos 4 meses",
    location: "São Paulo e Região",
    description: "Desenvolvimento backend para plataforma de recompensas e fidelização.",
    technologies: [".NET Core 2.2", "ASP.NET Core", "Dapper", "AngularJS", "Azure Service Bus", "Redis"],
    icon: <Terminal className="h-5 w-5" />,
    category: "backend",
    highlights: [
      "Participação ativa na criação da arquitetura",
      "Princípios Twelve-Factor App e DDD",
      "Foco em manutenibilidade e performance"
    ]
  }
];

// Componente de texto digitando (typing effect)
function TypingText({ text, speed = 50, className = "" }: { text: string, speed?: number, className?: string }) {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);

      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text, speed]);

  return <span className={className}>{displayText}</span>;
}

// Componente de linha do tempo
function TimelineItem({ experience, isLast }: { experience: any, isLast: boolean }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="flex group">
      {/* Linha do tempo */}
      <div className="flex flex-col items-center mr-4">
        <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 border-green-400/30 bg-[#0f131c] group-hover:bg-green-400/10 transition-all duration-300 ${isExpanded ? 'bg-green-400/20' : ''}`}>
          {experience.icon}
        </div>
        {!isLast && (
          <div className="w-1 h-full bg-green-400/20 mt-2 group-hover:bg-green-400/40 transition-all duration-300"></div>
        )}
      </div>

      {/* Conteúdo */}
      <div
        className={`flex-1 pb-10 transition-all duration-300 ${isExpanded ? 'bg-[#0f131c] rounded-lg p-6' : 'cursor-pointer'}`}
        onClick={() => !isExpanded && setIsExpanded(true)}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-2">
          <h3 className="text-xl font-bold text-green-400">{experience.position}</h3>
          <span className="text-sm text-brand-muted mt-1 md:mt-0">{experience.period} • {experience.duration}</span>
        </div>

        <div className="flex items-center text-brand-muted mb-3">
          <Building className="h-4 w-4 mr-2" />
          <span>{experience.company}</span>
          <span className="mx-2">•</span>
          <span>{experience.location}</span>
        </div>

        <p className="text-brand-muted mb-4">{experience.description}</p>

        {isExpanded && (
          <div className="mt-4">
            <h4 className="text-lg font-semibold text-white mb-2">Principais Realizações:</h4>
            <ul className="space-y-2 mb-4">
              {experience.highlights.map((highlight: string, index: number) => (
                <li key={index} className="flex items-start">
                  <span className="text-green-400 mr-2">▹</span>
                  <span className="text-brand-muted">{highlight}</span>
                </li>
              ))}
            </ul>

            <h4 className="text-lg font-semibold text-white mb-2">Tecnologias:</h4>
            <div className="flex flex-wrap gap-2">
              {experience.technologies.map((tech: string, index: number) => (
                <span
                  key={index}
                  className="text-xs rounded-md border border-green-400/20 px-2 py-1 bg-green-400/5 text-green-300"
                >
                  {tech}
                </span>
              ))}
            </div>

            <button
              onClick={() => setIsExpanded(false)}
              className="mt-4 text-sm text-green-400 hover:text-green-300 transition-colors"
            >
              [Recolher]
            </button>
          </div>
        )}

        {!isExpanded && (
          <button
            onClick={() => setIsExpanded(true)}
            className="text-sm text-green-400 hover:text-green-300 transition-colors"
          >
            [Expandir detalhes]
          </button>
        )}
      </div>
    </div>
  );
}

// Filtros por categoria
function ExperienceFilters({ activeFilter, setActiveFilter }: { activeFilter: string, setActiveFilter: (filter: string) => void }) {
  const filters = [
    { id: 'all', label: 'Todas' },
    { id: 'ai', label: 'IA & Inovação' },
    { id: 'backend', label: 'Back-end' },
    { id: 'fullstack', label: 'Full-stack' }
  ];

  return (
    <div className="flex flex-wrap gap-3 mb-8">
      {filters.map(filter => (
        <button
          key={filter.id}
          onClick={() => setActiveFilter(filter.id)}
          className={`px-4 py-2 rounded-lg border text-sm transition-all duration-300 ${activeFilter === filter.id
            ? 'border-green-400 bg-green-400/10 text-green-400'
            : 'border-green-400/20 text-brand-muted hover:border-green-400/40 hover:text-green-300'
            }`}
        >
          {filter.label}
        </button>
      ))}
    </div>
  );
}

export default function ExperienceTimeline() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  const filteredExperiences = activeFilter === 'all'
    ? experiences
    : experiences.filter(exp => exp.category === activeFilter);

  return (
    <main className="min-h-screen relative overflow-x-hidden bg-[#0B0E14] text-white font-mono">
      {/* Efeito de matriz no fundo */}
      <div className="fixed inset-0 -z-10 opacity-20">
        <canvas id="matrixCanvas" className="w-full h-full" />
      </div>

      {/* Glow effects */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-24 h-80 w-80 rounded-full blur-[90px] opacity-30" style={{ background: 'radial-gradient(circle,#7F5AF0,transparent 60%)' }} />
        <div className="absolute top-40 right-0 h-96 w-96 rounded-full blur-[120px] opacity-25" style={{ background: 'radial-gradient(circle,#2CB67D,transparent 60%)' }} />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full blur-[100px] opacity-25" style={{ background: 'radial-gradient(circle,#FF8906,transparent 60%)' }} />

        {/* Linhas de grade */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiPjxkZWZzPjxwYXR0ZXJuIGlkPSJwYXR0ZXJuIiB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHBhdHRlcm5UcmFuc2Zvcm09InJvdGF0ZSg0NSkiPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiMxMTE0MWMiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjcGF0dGVybikiLz48L3N2Zz4=')] opacity-20"></div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-[#0B0E14CC] border-b border-green-400/10">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors">
            <ChevronLeft className="h-5 w-5" />
            <span>Voltar para Home</span>
          </Link>
          <div className="flex items-center gap-3">
            <Code className="h-6 w-6 text-brand-secondary" />
            <span className="font-semibold tracking-wide text-green-400">~/experiencia</span>
          </div>
        </div>
      </header>

      {/* Conteúdo principal */}
      <section className="relative py-12">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-tr from-brand-primary via-brand-secondary to-brand-accent">
                Linha do Tempo Profissional
              </span>
            </h1>
            {loaded && (
              <p className="text-lg text-brand-muted max-w-2xl mx-auto">
                <TypingText
                  text="Uma jornada através dos meus projetos e experiências em desenvolvimento de software."
                  speed={30}
                />
              </p>
            )}
          </div>

          {/* Filtros */}
          <ExperienceFilters activeFilter={activeFilter} setActiveFilter={setActiveFilter} />

          {/* Timeline */}
          <div className="relative">
            {filteredExperiences.map((experience, index) => (
              <TimelineItem
                key={experience.id}
                experience={experience}
                isLast={index === filteredExperiences.length - 1}
              />
            ))}
          </div>

          {/* Resumo de competências */}
          <div className="mt-16 p-6 rounded-2xl border border-green-400/20 bg-[#0f131c]">
            <h2 className="text-2xl font-bold text-green-400 mb-6">Competências Principais</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Back-end & Cloud</h3>
                <ul className="space-y-2">
                  <li className="flex items-center"><span className="text-green-400 mr-2">■</span> .NET Core / .NET 5+</li>
                  <li className="flex items-center"><span className="text-green-400 mr-2">■</span> Arquitetura Limpa & DDD</li>
                  <li className="flex items-center"><span className="text-green-400 mr-2">■</span> Microsserviços</li>
                  <li className="flex items-center"><span className="text-green-400 mr-2">■</span> Azure & AWS</li>
                  <li className="flex items-center"><span className="text-green-400 mr-2">■</span> Docker & Kubernetes</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Front-end & Banco de Dados</h3>
                <ul className="space-y-2">
                  <li className="flex items-center"><span className="text-green-400 mr-2">■</span> React & Angular</li>
                  <li className="flex items-center"><span className="text-green-400 mr-2">■</span> TypeScript</li>
                  <li className="flex items-center"><span className="text-green-400 mr-2">■</span> SQL Server & PostgreSQL</li>
                  <li className="flex items-center"><span className="text-green-400 mr-2">■</span> MongoDB & RavenDB</li>
                  <li className="flex items-center"><span className="text-green-400 mr-2">■</span> Redis</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-green-400/10 mt-20">
        <div className="mx-auto max-w-6xl px-6 py-8 text-sm text-brand-muted flex flex-col md:flex-row items-center justify-between gap-4 font-mono">
          <div className="flex items-center gap-2">
            <span>© {new Date().getFullYear()} Antares Tecnologia GTA</span>
          </div>
          <div className="flex items-center gap-4">
            <span>thiagoantares@outlook.com</span>
          </div>
        </div>
      </footer>
    </main>
  );
}